import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageBody } from '../../elements/page-body';
import { useHttpClient } from '../../shared/http-client';
import { motion } from 'framer-motion';
import { SelectAsync } from '../../elements/select-async';
import {
    BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from 'recharts';

type Period = 'day' | 'week' | 'month' | 'year';

interface DashboardData {
    current_period: { type: string; start_date: string; end_date: string; metrics: any };
    previous_period: { start_date: string; end_date: string; metrics: any };
    comparisons: any;
}

const COLORS = {
    excellent: '#10b981', good: '#6ee7b7', notGood: '#fbbf24',
    bad: '#f59e0b', veryBad: '#ef4444', unknown: '#9ca3af'
};

export const Dashboard2 = () => {
    const { t } = useTranslation();
    const { api, state, options } = useHttpClient();
    const [period, setPeriod] = useState<Period>('month');
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedDriver, setSelectedDriver] = useState<any>(null);
    const [selectedVehicle, setSelectedVehicle] = useState<any>(null);

    // Handle API responses
    useEffect(() => {
        if (state.data) {
            if (state.path.includes('/fleet/dashboard/')) {
                setData(state.data);
                setLoading(false);
            }
        }
    }, [state]);

    // Load data when filters change
    useEffect(() => {
        loadData();
    }, [period, selectedDriver, selectedVehicle]);

    const loadData = () => {
        setLoading(true);

        // Build query parameters
        const params = new URLSearchParams();
        params.append('period', period);

        if (selectedDriver) {
            params.append('driver_id', selectedDriver.id);
        }

        if (selectedVehicle) {
            params.append('vehicle_id', selectedVehicle.id);
        }

        api(`/fleet/dashboard/?${params.toString()}`, 'GET');
    };

    const SimpleCard = ({ title, current, previous, change, changePercent, suffix = '', isInverted = false }: any) => {
        const isPositive = isInverted ? (change || 0) < 0 : (change || 0) > 0;
        const changeColor = (change || 0) === 0 ? 'text-gray-500' : isPositive ? 'text-green-600' : 'text-red-600';

        return (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-5">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{title}</h3>

                <div className="flex items-baseline justify-between">
                    <div className="flex items-baseline space-x-2">
                        <span className="text-3xl font-bold text-gray-900">
                            {typeof current === 'number' ? current.toLocaleString('en-US', { maximumFractionDigits: 2 }) : current}
                        </span>
                        {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
                    </div>

                    {changePercent !== undefined && (
                        <div className={`flex items-center space-x-1 ${changeColor}`}>
                            <span className="text-sm font-bold">{changePercent > 0 ? '+' : ''}{changePercent?.toFixed(1)}%</span>
                        </div>
                    )}
                </div>

                {previous !== undefined && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-500">{t('dashboard.label.previous-period')}</span>
                            <span className="font-semibold text-gray-700">
                                {typeof previous === 'number' ? previous.toLocaleString('en-US', { maximumFractionDigits: 2 }) : previous} {suffix}
                            </span>
                        </div>
                    </div>
                )}
            </motion.div>
        );
    };

    const TotalScoreCard = ({ score, change, maxScore = 10, ecoScore, efficiencyScore, safetyScore, ecoChange, efficiencyChange, safetyChange, accidents, sanctions, accidentsChange, sanctionsChange, aggressiveEvents, aggressiveEventsChange }: any) => {
        const percentage = score ? (score / maxScore) * 100 : 0;
        const color = percentage >= 90 ? '#10b981' : percentage >= 75 ? '#6ee7b7' : percentage >= 60 ? '#fbbf24' : '#ef4444';
        const isPositive = (change || 0) > 0;
        const hasScoreData = score !== null && score !== undefined;

        return (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
                className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-2xl p-8 text-white">
                <h2 className="text-xl font-bold mb-6 text-center uppercase tracking-wider">Performance Scores</h2>

                {/* Main Score */}
                <div className="flex items-center justify-center mb-6">
                    <div className="relative w-48 h-48">
                        <svg className="transform -rotate-90 w-48 h-48">
                            <circle cx="96" cy="96" r="88" stroke="rgba(255,255,255,0.2)" strokeWidth="16" fill="none" />
                            {hasScoreData && (
                                <circle cx="96" cy="96" r="88" stroke={color} strokeWidth="16" fill="none"
                                    strokeDasharray={`${2 * Math.PI * 88}`}
                                    strokeDashoffset={`${2 * Math.PI * 88 * (1 - percentage / 100)}`}
                                    strokeLinecap="round" className="transition-all duration-1000 ease-out" />
                            )}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-6xl font-black">{hasScoreData ? score?.toFixed(2) : 'N/A'}</div>
                            <div className="text-sm opacity-80">/ {maxScore}</div>
                        </div>
                    </div>
                </div>

                {hasScoreData && (
                    <div className="flex justify-center items-center space-x-3 mb-6">
                        <div className="text-center px-4 py-2 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
                            <div className="text-xs opacity-80">Change</div>
                            <div className={`text-lg font-bold flex items-center justify-center ${isPositive ? 'text-green-100' : 'text-red-200'}`}>
                                {change > 0 ? '+' : ''}{change?.toFixed(2)}
                            </div>
                        </div>
                    </div>
                )}

                {/* Performance Metrics Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    {/* Scores */}
                    <div>
                        <div className="text-sm font-medium mb-3 text-center opacity-90">Scores</div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Eco</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{ecoScore?.toFixed(1) || 'N/A'}</div>
                                    <div className={`text-xs ${ecoChange > 0 ? 'text-green-100' : 'text-red-200'}`}>
                                        {ecoChange > 0 ? '+' : ''}{ecoChange?.toFixed(1)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Efficiency</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{efficiencyScore?.toFixed(1) || 'N/A'}</div>
                                    <div className={`text-xs ${efficiencyChange > 0 ? 'text-green-100' : 'text-red-200'}`}>
                                        {efficiencyChange > 0 ? '+' : ''}{efficiencyChange?.toFixed(1)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Safety</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{safetyScore?.toFixed(1) || 'N/A'}</div>
                                    <div className={`text-xs ${safetyChange > 0 ? 'text-green-100' : 'text-red-200'}`}>
                                        {safetyChange > 0 ? '+' : ''}{safetyChange?.toFixed(1)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Safety & Events */}
                    <div>
                        <div className="text-sm font-medium mb-3 text-center opacity-90">Safety & Events</div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Accidents</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{accidents || 0}</div>
                                    <div className={`text-xs ${accidentsChange < 0 ? 'text-green-100' : accidentsChange > 0 ? 'text-red-200' : 'text-gray-300'}`}>
                                        {accidentsChange < 0 ? '' : accidentsChange > 0 ? '+' : ''}{accidentsChange || 0}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Sanctions</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{sanctions || 0}</div>
                                    <div className={`text-xs ${sanctionsChange < 0 ? 'text-green-100' : sanctionsChange > 0 ? 'text-red-200' : 'text-gray-300'}`}>
                                        {sanctionsChange < 0 ? '' : sanctionsChange > 0 ? '+' : ''}{sanctionsChange || 0}
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-between items-center bg-white bg-opacity-10 rounded-lg px-3 py-2">
                                <span className="text-xs opacity-80">Aggressive</span>
                                <div className="text-right">
                                    <div className="text-sm font-bold">{aggressiveEvents || 0}</div>
                                    <div className={`text-xs ${aggressiveEventsChange < 0 ? 'text-green-100' : aggressiveEventsChange > 0 ? 'text-red-200' : 'text-gray-300'}`}>
                                        {aggressiveEventsChange < 0 ? '' : aggressiveEventsChange > 0 ? '+' : ''}{aggressiveEventsChange || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {!hasScoreData && (
                    <div className="text-center text-sm opacity-70">
                        Insufficient data for score calculation
                    </div>
                )}
            </motion.div>
        );
    };

    const OverviewCard = ({ currentDistance, previousDistance, currentFuel, previousFuel, currentScore, previousScore }: any) => {
        const distanceChange = previousDistance ? ((currentDistance - previousDistance) / previousDistance * 100) : 0;
        const fuelChange = previousFuel ? ((currentFuel - previousFuel) / previousFuel * 100) : 0;
        const scoreChange = previousScore ? ((currentScore - previousScore) / previousScore * 100) : 0;

        const isDistancePositive = distanceChange > 0;
        const isFuelPositive = fuelChange < 0; // Less fuel is better
        const isScorePositive = scoreChange > 0;

        return (
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-2xl p-8 text-white">
                <h2 className="text-2xl font-bold mb-8 text-center uppercase tracking-wider">{t('dashboard.label.fleet-overview')}</h2>

                {/* Current Period - Large */}
                <div className="grid grid-cols-3 gap-6 mb-8">
                    <div className="text-center">
                        <div className="text-5xl font-black mb-2">{(currentDistance / 1000).toFixed(1)}</div>
                        <div className="text-sm font-medium mb-2 opacity-90">{t('dashboard.label.distance-km')}</div>
                        <div className={`text-sm flex items-center justify-center font-semibold ${isDistancePositive ? 'text-green-100' : 'text-red-200'}`}>
                            {distanceChange > 0 ? '+' : ''}{distanceChange?.toFixed(1)}%
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-5xl font-black mb-2">{currentFuel?.toFixed(1)}</div>
                        <div className="text-sm font-medium mb-2 opacity-90">{t('dashboard.label.fuel-l')}</div>
                        <div className={`text-sm flex items-center justify-center font-semibold ${isFuelPositive ? 'text-green-100' : 'text-red-200'}`}>
                            {fuelChange > 0 ? '+' : ''}{fuelChange?.toFixed(1)}%
                        </div>
                    </div>
                    <div className="text-center">
                        <div className="text-5xl font-black mb-2">{currentScore?.toFixed(1) || 'N/A'}</div>
                        <div className="text-sm font-medium mb-2 opacity-90">{t('dashboard.label.total-score')}</div>
                        <div className={`text-sm flex items-center justify-center font-semibold ${isScorePositive ? 'text-green-100' : 'text-red-200'}`}>
                            {scoreChange > 0 ? '+' : ''}{scoreChange?.toFixed(1)}%
                        </div>
                    </div>
                </div>

                {/* Previous Period - Small */}
                <div className="pt-6 border-t border-white border-opacity-30">
                    <div className="text-sm font-medium mb-4 text-center opacity-70">{t('dashboard.label.previous-period')}</div>
                    <div className="grid grid-cols-3 gap-6">
                        <div className="text-center">
                            <div className="text-2xl font-semibold mb-1 opacity-80">{(previousDistance / 1000).toFixed(1)}</div>
                            <div className="text-xs opacity-60">{t('dashboard.label.distance-km')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-semibold mb-1 opacity-80">{previousFuel?.toFixed(1)}</div>
                            <div className="text-xs opacity-60">{t('dashboard.label.fuel-l')}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl font-semibold mb-1 opacity-80">{previousScore?.toFixed(1) || 'N/A'}</div>
                            <div className="text-xs opacity-60">{t('dashboard.label.total-score')}</div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };

    if (loading || !data) {
        return (
            <PageBody>
                <div className="flex items-center justify-center h-96">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                </div>
            </PageBody>
        );
    }

    const { current_period, previous_period, comparisons } = data;
    const metrics = current_period.metrics;
    const prevMetrics = previous_period.metrics;

    // Function to format date as "day month year"
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'numeric', year: 'numeric' });
    };

    const ecoData = Object.entries(metrics.eco_category_distribution || {}).map(([key, value]) => ({
        name: key.replace(/_/g, ' '),
        value: value as number
    }));

    const aggressiveEventsData = Object.entries(metrics.aggressive_events?.by_type || {}).map(([key, value]) => ({
        name: key.replace(/_/g, ' '),
        value: value as number
    }));

    const performanceData = [
        { metric: 'Eco Score', current: metrics.eco_score || 0, previous: prevMetrics.eco_score || 0 },
        { metric: 'Efficiency', current: metrics.efficiency_score || 0, previous: prevMetrics.efficiency_score || 0 },
        { metric: 'Safety', current: metrics.safety_score || 0, previous: prevMetrics.safety_score || 0 }
    ];

    return (
        <PageBody>
            <div className="space-y-6">
                {/* Header */}
                <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
                    className="flex justify-between items-center bg-white rounded-2xl shadow-lg p-6">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900">{t('common.label.dashboard-tilte')}</h1>
                        <div className="flex items-center space-x-6 mt-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                    <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{t('dashboard.label.current-period')}</span>
                                </div>
                                <span className="text-sm font-medium text-gray-800 mt-1 block">{formatDate(current_period.start_date)} → {formatDate(current_period.end_date)}</span>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                                <div className="flex items-center">
                                    <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.label.previous-period')}</span>
                                </div>
                                <span className="text-sm font-medium text-gray-700 mt-1 block">{formatDate(previous_period.start_date)} → {formatDate(previous_period.end_date)}</span>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl shadow-sm border border-gray-100 w-full max-w-2xl">
                        <div className="flex flex-col space-y-4">
                            {/* Period Selector - Versione originale */}
                            <div className="flex space-x-2">
                                {(['day', 'week', 'month', 'year'] as Period[]).map((p) => (
                                    <button key={p} onClick={() => setPeriod(p)}
                                        className={`px-6 py-2 rounded-xl font-semibold transition-all ${period === p ? 'bg-blue-600 text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}>
                                        {t(`dashboard.label.${p}`)}
                                    </button>
                                ))}
                            </div>

                            {/* Filters */}
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div>
                                        <SelectAsync
                                            name="driver"
                                            label="dashboard.label.driver"
                                            placeholder="dashboard.label.search-placeholder-driver"
                                            items={async (value: string) => {
                                                return await options(
                                                    `/fleet/drivers/?search=${value}`
                                                );
                                            }}
                                            value={selectedDriver}
                                            optionsLabel="full_name"
                                            onChange={(value: any) => {
                                                setSelectedDriver(value || null);
                                            }}
                                            noMargin
                                        />
                                    </div>
                                    <div>
                                        <SelectAsync
                                            name="vehicle"
                                            label="dashboard.label.vehicle"
                                            placeholder="dashboard.label.search-placeholder-vehicle"
                                            items={async (value: string) => {
                                                return await options(
                                                    `/fleet/vehicles/?plate=${value}`
                                                );
                                            }}
                                            value={selectedVehicle}
                                            optionsLabel="plate"
                                            onChange={(value: any) => {
                                                setSelectedVehicle(value || null);
                                            }}
                                            noMargin
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Overview Header */}
                <OverviewCard
                    currentDistance={metrics.total_distance}
                    previousDistance={prevMetrics.total_distance}
                    currentFuel={metrics.total_fuel_used}
                    previousFuel={prevMetrics.total_fuel_used}
                    currentScore={metrics.total_score}
                    previousScore={prevMetrics.total_score}
                />

                {/* Fleet Overview */}
                {!selectedDriver && !selectedVehicle ? <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SimpleCard title={t('dashboard.label.total-vehicles')} current={metrics.vehicles_total} previous={prevMetrics.vehicles_total}
                            change={comparisons.vehicles_total_change_absolute} changePercent={comparisons.vehicles_total_change_percent} />
                        <SimpleCard title={t('dashboard.label.active-vehicles')} current={metrics.vehicles_active} previous={prevMetrics.vehicles_active}
                            change={comparisons.vehicles_active_change_absolute} changePercent={comparisons.vehicles_active_change_percent} />
                        <SimpleCard title={t('dashboard.label.total-drivers')} current={metrics.drivers_total} previous={prevMetrics.drivers_total}
                            change={comparisons.drivers_total_change_absolute} changePercent={comparisons.drivers_total_change_percent} />
                        <SimpleCard title={t('dashboard.label.active-drivers')} current={metrics.drivers_active} previous={prevMetrics.drivers_active}
                            change={comparisons.drivers_active_change_absolute} changePercent={comparisons.drivers_active_change_percent} />
                    </div>
                </div> : ''}

                {/* Trip Metrics */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.trip-metrics')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <SimpleCard title={t('dashboard.label.total-trips')} current={metrics.trips_count} previous={prevMetrics.trips_count}
                            change={comparisons.trips_count_change_absolute} changePercent={comparisons.trips_count_change_percent} />
                        <SimpleCard title={t('dashboard.label.total-distance')} current={(metrics.total_distance / 1000).toFixed(1)} previous={(prevMetrics.total_distance / 1000).toFixed(1)}
                            change={comparisons.total_distance_change_absolute} changePercent={comparisons.total_distance_change_percent} suffix="km" />
                        <SimpleCard title={t('dashboard.label.total-duration')} current={metrics.total_duration_display} previous={prevMetrics.total_duration_display}
                            change={comparisons.total_duration_change_absolute} changePercent={comparisons.total_duration_change_percent} />
                        <SimpleCard title={t('dashboard.label.total-fuel')} current={metrics.total_fuel_used?.toFixed(1)} previous={prevMetrics.total_fuel_used?.toFixed(1)}
                            change={comparisons.total_fuel_used_change_absolute} changePercent={comparisons.total_fuel_used_change_percent} suffix="L" isInverted />
                        <SimpleCard title={t('dashboard.label.fuel-per-100km')} current={metrics.fuel_per_100km?.toFixed(2)} previous={prevMetrics.fuel_per_100km?.toFixed(2)}
                            change={comparisons.fuel_per_100km_change_absolute} changePercent={comparisons.fuel_per_100km_change_percent} suffix="L" isInverted />
                    </div>

                    {/* Trip Metrics Charts */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        {/* Distance vs Expected */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.label.distance-actual-vs-expected')}</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={[
                                    { name: t('dashboard.label.current-period'), actual: metrics.total_distance / 1000, expected: metrics.total_expected_distance / 1000 },
                                    { name: t('dashboard.label.previous-period'), actual: prevMetrics.total_distance / 1000, expected: prevMetrics.total_expected_distance / 1000 }
                                ]}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis label={{ value: t('dashboard.label.distance-km'), angle: -90, position: 'insideLeft' }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="actual" fill="#3b82f6" name={t('dashboard.label.actual-distance')} radius={[8, 8, 0, 0]} />
                                    <Bar dataKey="expected" fill="#10b981" name={t('dashboard.label.expected-distance')} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </motion.div>

                        {/* Duration vs Expected */}
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.label.duration-actual-vs-expected')}</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={[
                                    {
                                        name: t('dashboard.label.current-period'),
                                        actual: (metrics.duration_comparison?.actual || 0) / 60,
                                        expected: (metrics.duration_comparison?.expected || 0) / 60,
                                        actualDisplay: metrics.duration_comparison?.actual_display || '0h 0m 0s',
                                        expectedDisplay: metrics.duration_comparison?.expected_display || '0h 0m 0s'
                                    },
                                    {
                                        name: t('dashboard.label.previous-period'),
                                        actual: (prevMetrics.duration_comparison?.actual || 0) / 60,
                                        expected: (prevMetrics.duration_comparison?.expected || 0) / 60,
                                        actualDisplay: prevMetrics.duration_comparison?.actual_display || '0h 0m 0s',
                                        expectedDisplay: prevMetrics.duration_comparison?.expected_display || '0h 0m 0s'
                                    }
                                ]}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis label={{ value: t('dashboard.label.duration-minutes'), angle: -90, position: 'insideLeft' }} />
                                    <Tooltip
                                        formatter={(value: any, name: string, props: any) => {
                                            if (name === t('dashboard.label.actual-duration')) return props.payload.actualDisplay;
                                            if (name === t('dashboard.label.expected-duration')) return props.payload.expectedDisplay;
                                            return value;
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="actual" fill="#3b82f6" name={t('dashboard.label.actual-duration')} radius={[8, 8, 0, 0]} />
                                    <Bar dataKey="expected" fill="#10b981" name={t('dashboard.label.expected-duration')} radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </motion.div>
                    </div>
                </div>


                {/* Safety Metrics */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.safety-metrics')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SimpleCard title={t('dashboard.label.accidents')} current={metrics.accidents_count} previous={prevMetrics.accidents_count}
                            change={comparisons.accidents_count_change_absolute} changePercent={comparisons.accidents_count_change_percent} isInverted />
                        <SimpleCard title={t('dashboard.label.sanctions')} current={metrics.sanctions_count} previous={prevMetrics.sanctions_count}
                            change={comparisons.sanctions_count_change_absolute} changePercent={comparisons.sanctions_count_change_percent} isInverted />
                        <SimpleCard title={t('dashboard.label.accident-rate')} current={metrics.accident_rate_per_100k_km?.toFixed(2) || '0'}
                            previous={prevMetrics.accident_rate_per_100k_km?.toFixed(2) || '0'}
                            change={comparisons.accident_rate_per_100k_km_change_absolute}
                            changePercent={comparisons.accident_rate_per_100k_km_change_percent} suffix="/100k km" isInverted />
                        <SimpleCard title={t('dashboard.label.sanction-rate')} current={metrics.sanction_rate_per_100k_km?.toFixed(2) || '0'}
                            previous={prevMetrics.sanction_rate_per_100k_km?.toFixed(2) || '0'}
                            change={comparisons.sanction_rate_per_100k_km_change_absolute}
                            changePercent={comparisons.sanction_rate_per_100k_km_change_percent} suffix="/100k km" isInverted />
                    </div>
                </div>

                {/* Aggressive Events */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.aggressive-driving-events')}</h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                            className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow p-5">
                            <div className="flex justify-between items-start mb-4">
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.label.total-aggressive-events')}</h3>
                                <div className={`flex items-center space-x-1 ${(comparisons.aggressive_events_total_change_absolute || 0) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    <span className="text-sm font-bold">
                                        {comparisons.aggressive_events_total_change_percent > 0 ? '+' : ''}{comparisons.aggressive_events_total_change_percent?.toFixed(1)}%
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-baseline justify-between mb-4">
                                <div className="flex items-baseline space-x-2">
                                    <span className="text-3xl font-bold text-gray-900">
                                        {metrics.aggressive_events?.total || 0}
                                    </span>
                                </div>
                            </div>

                            <div className="mt-2 pt-2 border-t border-gray-100">
                                <div className="flex justify-between items-center text-xs mb-2">
                                    <span className="text-gray-500">{t('dashboard.label.previous-period')}</span>
                                    <span className="font-semibold text-gray-700">
                                        {prevMetrics.aggressive_events?.total || 0}
                                    </span>
                                </div>
                            </div>

                            {/* By Type Breakdown */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">{t('dashboard.label.by-type')}</h4>
                                <div className="space-y-2">
                                    {Object.entries(metrics.aggressive_events?.by_type || {}).map(([key, value]) => (
                                        <div key={key} className="flex justify-between items-center text-xs">
                                            <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                            <span className="font-semibold text-gray-700">{value as number}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Aggressive Events by Type Chart */}
                        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-lg p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.label.aggressive-events-by-type')}</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={aggressiveEventsData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={false} />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#f59e0b" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </motion.div>
                    </div>
                </div>

                {/* Performance Scores */}
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.performance-scores')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <SimpleCard title={t('dashboard.label.eco-score')} current={metrics.eco_score?.toFixed(2)} previous={prevMetrics.eco_score?.toFixed(2)}
                            change={comparisons.eco_score_change_absolute} changePercent={comparisons.eco_score_change_percent} suffix="/ 10" />
                        <SimpleCard title={t('dashboard.label.efficiency-score')} current={metrics.efficiency_score?.toFixed(2)} previous={prevMetrics.efficiency_score?.toFixed(2)}
                            change={comparisons.efficiency_score_change_absolute} changePercent={comparisons.efficiency_score_change_percent} suffix="/ 10" />
                        <SimpleCard title={t('dashboard.label.safety-score')} current={metrics.safety_score?.toFixed(2)} previous={prevMetrics.safety_score?.toFixed(2)}
                            change={comparisons.safety_score_change_absolute} changePercent={comparisons.safety_score_change_percent} suffix="/ 10" />
                        <SimpleCard title={t('dashboard.label.total-score')} current={metrics.total_score}
                            previous={prevMetrics.total_score}
                            change={comparisons.total_score_change_absolute}
                            changePercent={comparisons.total_score_change_percent} suffix="/ 10" />
                    </div>
                </div>

                {/* Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Eco Distribution */}
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
                        className="bg-white rounded-2xl shadow-lg p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.label.eco-category-distribution')}</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie data={ecoData} cx="50%" cy="50%" labelLine={false}
                                    label={({ name, percent }: { name: string; percent: number }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100} fill="#8884d8" dataKey="value">
                                    {ecoData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={
                                            entry.name.includes('EXCELLENT') ? COLORS.excellent :
                                                entry.name.includes('GOOD') && !entry.name.includes('NOT') ? COLORS.good :
                                                    entry.name.includes('NOT GOOD') ? COLORS.notGood :
                                                        entry.name.includes('BAD') && !entry.name.includes('VERY') ? COLORS.bad :
                                                            entry.name.includes('VERY BAD') ? COLORS.veryBad : COLORS.unknown
                                        } />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </motion.div>

                    {/* Performance Comparison */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                        className="bg-white rounded-2xl shadow-lg p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">{t('dashboard.label.performance-current-vs-previous')}</h3>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Current Period */}
                            <div>
                                <h4 className="text-sm font-semibold text-blue-600 mb-2 text-center">{t('dashboard.label.current-period')}</h4>
                                <ResponsiveContainer width="100%" height={250}>
                                    <RadarChart data={performanceData}>
                                        <PolarGrid />
                                        <PolarAngleAxis dataKey="metric" />
                                        <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} />
                                        <Radar name="Current" dataKey="current" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                                        <Tooltip />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                            {/* Previous Period */}
                            <div>
                                <h4 className="text-sm font-semibold text-green-600 mb-2 text-center">{t('dashboard.label.previous-period')}</h4>
                                <ResponsiveContainer width="100%" height={250}>
                                    <RadarChart data={performanceData}>
                                        <PolarGrid />
                                        <PolarAngleAxis dataKey="metric" />
                                        <PolarRadiusAxis angle={90} domain={[0, 10]} tick={false} />
                                        <Radar name="Previous" dataKey="previous" stroke="#10b981" fill="#10b981" fillOpacity={0.6} />
                                        <Tooltip />
                                    </RadarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </motion.div>

                </div>

                {/* Averages Section - Only show when no filters are active */}
                {!selectedDriver && !selectedVehicle ?
                    <>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.averages-per-active-vehicle')}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <SimpleCard title={t('dashboard.label.avg-trips')} current={metrics.averages_per_active_vehicle?.trips}
                                    previous={prevMetrics.averages_per_active_vehicle?.trips}
                                    change={comparisons.averages_per_active_vehicle_trips_change_absolute}
                                    changePercent={comparisons.averages_per_active_vehicle_trips_change_percent} />
                                <SimpleCard title={t('dashboard.label.avg-distance')} current={(metrics.averages_per_active_vehicle?.distance / 1000)?.toFixed(1)}
                                    previous={(prevMetrics.averages_per_active_vehicle?.distance / 1000)?.toFixed(1)}
                                    change={comparisons.averages_per_active_vehicle_distance_change_absolute}
                                    changePercent={comparisons.averages_per_active_vehicle_distance_change_percent} suffix="km" />
                                <SimpleCard title={t('dashboard.label.avg-duration')} current={metrics.averages_per_active_vehicle?.travel_duration_display}
                                    previous={prevMetrics.averages_per_active_vehicle?.travel_duration_display}
                                    change={comparisons.averages_per_active_vehicle_travel_duration_change_absolute}
                                    changePercent={comparisons.averages_per_active_vehicle_travel_duration_change_percent} />
                                <SimpleCard title={t('dashboard.label.avg-fuel')} current={metrics.averages_per_active_vehicle?.fuel_used?.toFixed(1)}
                                    previous={prevMetrics.averages_per_active_vehicle?.fuel_used?.toFixed(1)}
                                    change={comparisons.averages_per_active_vehicle_fuel_used_change_absolute}
                                    changePercent={comparisons.averages_per_active_vehicle_fuel_used_change_percent} suffix="L" isInverted />
                            </div>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('dashboard.label.averages-per-active-driver')}</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <SimpleCard title={t('dashboard.label.avg-trips')} current={metrics.averages_per_active_driver?.trips}
                                    previous={prevMetrics.averages_per_active_driver?.trips}
                                    change={comparisons.averages_per_active_driver_trips_change_absolute}
                                    changePercent={comparisons.averages_per_active_driver_trips_change_percent} />
                                <SimpleCard title={t('dashboard.label.avg-distance')} current={(metrics.averages_per_active_driver?.distance / 1000)?.toFixed(1)}
                                    previous={(prevMetrics.averages_per_active_driver?.distance / 1000)?.toFixed(1)}
                                    change={comparisons.averages_per_active_driver_distance_change_absolute}
                                    changePercent={comparisons.averages_per_active_driver_distance_change_percent} suffix="km" />
                                <SimpleCard title={t('dashboard.label.avg-duration')} current={metrics.averages_per_active_driver?.travel_duration_display}
                                    previous={prevMetrics.averages_per_active_driver?.travel_duration_display}
                                    change={comparisons.averages_per_active_driver_travel_duration_change_absolute}
                                    changePercent={comparisons.averages_per_active_driver_travel_duration_change_percent} />
                                <SimpleCard title={t('dashboard.label.avg-fuel')} current={metrics.averages_per_active_driver?.fuel_used?.toFixed(1)}
                                    previous={prevMetrics.averages_per_active_driver?.fuel_used?.toFixed(1)}
                                    change={comparisons.averages_per_active_driver_fuel_used_change_absolute}
                                    changePercent={comparisons.averages_per_active_driver_fuel_used_change_percent} suffix="L" isInverted />
                            </div>
                        </div>
                    </> : ''
                }
            </div>
        </PageBody>
    );
};
